import { useState } from 'react';
import { useNavigate } from 'react-router';
import { newHandoverId, SESSION_KINDS, waitingDays, type OpsPartner } from '@ecobox/firebase';
import { Badge, Button, Card, EmptyState, TimeInState, useNow, useToast } from '@ecobox/ui';
import { useOpenHandovers, usePartners, useWaitingUnits } from '../hooks/useHandover';
import { useBranch, useServices, useSession } from '../services';
import { useAllSessions, usePermissions, useSessionEngine } from '../sessions';

/**
 * Hand over: pick the partner, open a handover, scan the boxes in (05 §5.2, 12 §12.5). The sign
 * step is on the next screen. A session started here lives on the phone like a load session, so
 * a flat battery or a lost signal loses nothing.
 */
export function HandoverPage() {
  const branch = useBranch().current;
  const { uid } = useSession();
  const { writer } = useServices();
  const engine = useSessionEngine();
  const sessions = useAllSessions();
  const perms = usePermissions();
  const navigate = useNavigate();
  const toast = useToast();
  const partners = usePartners();
  const waiting = useWaitingUnits(perms.has('partner.handover') ? branch : null);
  const open = useOpenHandovers(perms.has('partner.handover') ? branch : null);
  const [busy, setBusy] = useState<string | null>(null);
  const now = useNow();

  if (!perms.has('partner.handover')) {
    return (
      <EmptyState title="Handing over is not part of your role" description="Ask your manager." />
    );
  }

  const mine = sessions.filter(
    (s) =>
      s.uid === uid &&
      s.finishedAt === undefined &&
      s.branchId === branch &&
      SESSION_KINDS[s.kind].direction === 'handover',
  );

  const resume = (handoverId: string, label: string, partnerId: string) => {
    if (!uid || !branch) return;
    const session = engine.start({
      kind: 'load_handover',
      targetId: handoverId,
      targetLabel: label,
      uid,
      branchId: branch,
      extra: { partnerId },
    });
    void navigate(`/handover/${session.id}`);
  };

  const begin = (partner: OpsPartner) => {
    if (!uid || !branch) return;
    setBusy(partner.id);
    try {
      const handoverId = newHandoverId();
      // The handover is named on the phone, so scanning can start before the server has it.
      writer.send('openHandover', { handoverId, partnerId: partner.id });
      resume(handoverId, partner.name, partner.id);
    } catch (err) {
      setBusy(null);
      toast.show({
        title: 'Handover not opened',
        message: err instanceof Error ? err.message : String(err),
        tone: 'danger',
      });
    }
  };

  // Open handovers on the server that this phone has no session for (started on another phone).
  const known = new Set(sessions.filter((s) => s.kind === 'load_handover').map((s) => s.targetId));
  const others = open.data.filter((h) => !known.has(h.id));

  return (
    <div className="ops-stack">
      <h1 className="ops-title">Hand over to a partner</h1>

      {mine.length > 0 ? (
        <ul className="ops-list" aria-label="Handovers in progress" data-tour="handovers-mine">
          {mine.map((s) => (
            <li key={s.id} className="ops-row" data-tone="waiting">
              <div className="ops-row__top">
                <strong>{s.targetLabel}</strong>
                <Badge tone="info">{s.scans.length} scanned</Badge>
              </div>
              <div className="ops-muted">
                started <TimeInState since={s.startedAt} suffix="ago" />
              </div>
              <Button
                size="touch"
                variant="cta"
                block
                onClick={() => void navigate(`/handover/${s.id}`)}
              >
                Continue
              </Button>
            </li>
          ))}
        </ul>
      ) : null}

      {others.length > 0 ? (
        <section
          className="ops-stack"
          aria-label="Open on another phone"
          data-tour="handovers-other"
        >
          <h2 className="ops-subtitle">Open on another phone</h2>
          <ul className="ops-list">
            {others.map((h) => (
              <li key={h.id} className="ops-row">
                <div className="ops-row__top">
                  <strong>{h.partnerName}</strong>
                  <Badge tone="neutral">{h.counts.units} loaded</Badge>
                </div>
                <Button
                  size="touch"
                  variant="secondary"
                  block
                  onClick={() => resume(h.id, h.partnerName, h.partnerId)}
                >
                  Continue here
                </Button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <h2 className="ops-subtitle" data-tour="handover-partners">
        Who is taking the boxes?
      </h2>
      {partners.error ? (
        <p role="alert" className="ops-error">
          {partners.error}
        </p>
      ) : null}
      {partners.data.length === 0 ? (
        <EmptyState
          title={partners.loading ? 'Loading…' : 'No partner is set up'}
          description="A coordinator adds haulers and couriers in the office (Settings → Coverage & partners)."
        />
      ) : (
        <ul className="ops-list" aria-label="Partners" data-tour="partner-list">
          {partners.data.map((p) => {
            const units = waiting.byPartner.get(p.id) ?? [];
            const ready = units.filter((u) => !u.problem).length;
            const oldest = units[0];
            return (
              <li key={p.id} className="ops-row">
                <div className="ops-row__top">
                  <strong>{p.name}</strong>
                  <Badge tone={p.kind === 'courier' ? 'info' : 'neutral'}>
                    {p.kind === 'courier' ? 'Courier' : 'Hauler'}
                  </Badge>
                </div>
                <div className="ops-muted">
                  {p.kind === 'courier'
                    ? 'Prime pieces: scan the piece labels.'
                    : units.length === 0
                      ? waiting.ready
                        ? 'Nothing waiting for this partner.'
                        : 'Checking what is waiting…'
                      : `${ready} ready to go${units.length > ready ? `, ${units.length - ready} not allowed yet` : ''}${
                          oldest ? ` · oldest waiting ${waitingDays(oldest.since, now)} day(s)` : ''
                        }`}
                </div>
                <Button
                  size="touch"
                  variant="primary"
                  block
                  loading={busy === p.id}
                  onClick={() => begin(p)}
                >
                  Start a handover
                </Button>
              </li>
            );
          })}
        </ul>
      )}
      {waiting.error ? (
        <Card>
          <p className="ops-muted" style={{ margin: 0 }}>
            The list of what is waiting is not available right now. You can still scan the boxes one
            by one; the server checks each one.
          </p>
        </Card>
      ) : null}
    </div>
  );
}
